/* The Campaigns table, derived. mockups/crm-v3-campaigns-v1.html §#campaigns.

   Same posture as listModel.js: every column is computed here, over data
   already in memory, and the view only draws. Two differences from the
   Contacts model are worth knowing:

   - The audience count for a campaign that has NOT been approved is resolved
     live through src/lib/segments.js, never read off the record. That is the
     whole argument of the approval bar (plan decision 4): you approve the
     number the resolver returned this second.
   - Replies are counted from the `interactions` collection, not from
     Mailchimp, which has no reply metric at all. The stored `results.replied`
     is only ever a fallback for a record written by something else.          */

import { relativeTime, barPct } from "./listModel.js";
import {
  CAMPAIGN_CHANNELS, CAMPAIGN_STATUSES, CAMPAIGN_STATUS_TONE,
  campaignStatus, isV3Campaign,
} from "../../lib/campaigns.js";
import { resolveSegment } from "../../lib/segments.js";

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const str = (v) => String(v ?? "").trim();
const DAY = 86_400_000;

/* ── dates ───────────────────────────────────────────────────────────────── */

const NY = "America/New_York";

/* A bare YYYY-MM-DD is a CALENDAR DATE, not an instant.

   `Date.parse("2026-09-02")` is midnight UTC, which in New York is 8pm on
   September 1st — so formatting an interaction's `date` field through the
   studio's timezone moved every one of them a day earlier. The two are
   genuinely different kinds of value and are formatted differently: a date is
   already in the studio's own calendar and needs no conversion, an instant
   does. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** "Sep 2" — the table's date. A calendar date as written; an instant in New York. */
export function shortDate(value) {
  const raw = str(value);
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return "";
  // A date-only value is read back in UTC, which is the calendar it was
  // written in — no shift.
  const zone = DATE_ONLY.test(raw) ? "UTC" : NY;
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, month: "short", day: "numeric" }).format(new Date(ms));
}

/** "2026-09" — the month "this month" compares against. */
export function nyMonth(value, now = new Date()) {
  const raw = str(value);
  if (DATE_ONLY.test(raw)) return raw.slice(0, 7);
  const ms = Date.parse(raw);
  const d = Number.isNaN(ms) ? now : new Date(ms);
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone: NY, year: "numeric", month: "2-digit" })
      .formatToParts(d).map((x) => [x.type, x.value]),
  );
  return `${p.year}-${p.month}`;
}

const whenOf = (ix) => str(ix?.at) || str(ix?.date);

/* ── check-ins (the running row) ─────────────────────────────────────────── */

/**
 * One-email outreach: `type: "message"`, `direction: "out"`, `outreach:
 * "hello"`. `/crm hello` writes these (slice 7); the field is defined in
 * docs/client-interactions-spec.md.
 */
export const isHello = (ix) =>
  str(ix?.type) === "message" && str(ix?.direction) === "out" && str(ix?.outreach) === "hello";

/** How long after a hello an inbound message still counts as its reply. */
export const REPLY_WINDOW_DAYS = 14;

/**
 * The Check-ins row: how many hellos went out this month, who the last one was
 * for and when, and how many of this month's hellos have been replied to.
 *
 * A reply is an inbound message from the SAME contact within a fortnight of
 * the hello. One reply closes one hello: a person who was written to twice and
 * answered once counts once, which is why this walks the hellos rather than
 * counting inbound messages.
 */
export function buildCheckIns({ interactions = [], contacts = [], now = new Date() } = {}) {
  const month = nyMonth(now.toISOString(), now);
  const nameById = new Map(contacts.map((c) => [str(c.id), str(c.name)]));

  const hellos = interactions.filter(isHello)
    .map((ix) => ({
      id: str(ix.id),
      at: whenOf(ix),
      contactId: str(asArray(ix.contactIds)[0]),
      summary: str(ix.summary),
      subject: str(ix.subject),
    }))
    .filter((h) => h.at)
    .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));

  const inbound = interactions.filter((ix) => str(ix?.type) === "message" && str(ix?.direction) === "in");

  /* One reply closes ONE hello.

     `some()` let a single inbound message answer every hello that contact had
     ever been sent, so writing to somebody three times and hearing back once
     read as three replies and the check-ins row claimed a response rate it had
     not earned. Pairing is greedy and oldest-first: each hello, in the order it
     went out, takes the earliest reply from that person that it has not
     already been claimed by an earlier hello. */
  const instant = (v) => {
    const raw = str(v);
    const ms = Date.parse(raw.length <= 10 ? `${raw}T00:00:00Z` : raw);
    return Number.isNaN(ms) ? null : ms;
  };
  const replies = inbound
    .map((ix) => ({ id: str(ix.id), at: instant(whenOf(ix)), contactIds: asArray(ix.contactIds).map(str) }))
    .filter((r) => r.at !== null)
    .sort((a, b) => a.at - b.at);

  const consumed = new Set();
  const answered = new Set();
  // Oldest hello first, so the earlier message gets the earlier answer.
  for (const hello of [...hellos].reverse()) {
    const from = instant(hello.at);
    if (from === null) continue;
    const match = replies.find((r) => (
      !consumed.has(r.id)
      && r.contactIds.includes(hello.contactId)
      && r.at >= from && r.at - from <= REPLY_WINDOW_DAYS * DAY
    ));
    if (!match) continue;
    consumed.add(match.id);
    answered.add(hello.id);
  }

  const thisMonth = hellos.filter((h) => nyMonth(h.at, now) === month);
  const rows = hellos.map((h) => ({
    ...h,
    name: nameById.get(h.contactId) || h.contactId || "Unknown",
    replied: answered.has(h.id),
  }));

  const last = rows[0] || null;
  return {
    total: hellos.length,
    thisMonth: thisMonth.length,
    replies: rows.filter((h) => h.replied && nyMonth(h.at, now) === month).length,
    last: last ? { name: last.name, at: last.at, rel: relativeTime(last.at, now) } : null,
    rows,
  };
}

/* ── replies to a campaign ───────────────────────────────────────────────── */

/** The first line of a message body, for the drawer's reply list. */
export const firstLine = (body, cap = 90) => {
  const line = str(String(body ?? "").split(/\n+/).find((l) => str(l)) || "");
  return line.length > cap ? `${line.slice(0, cap - 1)}…` : line;
};

/**
 * Inbound messages from the frozen recipients, after the send.
 *
 * Mailchimp counts opens, clicks, bounces and unsubscribes; it has no idea
 * whether anyone wrote back, so this is the one number in the results panel
 * that comes out of our own inbox.
 */
export function campaignReplies({ campaign, interactions = [], contacts = [], now = new Date() } = {}) {
  const sentAt = str(campaign?.send?.sentAt);
  const ids = new Set(asArray(campaign?.audience?.contactIds).map(str));
  if (!sentAt || !ids.size) return [];
  const from = Date.parse(sentAt);
  if (Number.isNaN(from)) return [];
  const nameById = new Map(contacts.map((c) => [str(c.id), str(c.name)]));

  return interactions
    .filter((ix) => str(ix?.type) === "message" && str(ix?.direction) === "in")
    .filter((ix) => asArray(ix.contactIds).map(str).some((id) => ids.has(id)))
    .map((ix) => ({ ix, at: whenOf(ix), ms: Date.parse(whenOf(ix)) }))
    .filter((r) => !Number.isNaN(r.ms) && r.ms >= from && r.ms <= now.getTime() + DAY)
    .sort((a, b) => b.ms - a.ms)
    .map(({ ix, at }) => ({
      id: str(ix.id),
      at,
      contactId: asArray(ix.contactIds).map(str).find((id) => ids.has(id)) || "",
      name: nameById.get(asArray(ix.contactIds).map(str).find((id) => ids.has(id))) || str(ix.from) || "Someone",
      line: firstLine(ix.summary) || firstLine(ix.body),
    }));
}

/* ── rows ────────────────────────────────────────────────────────────────── */

/**
 * The live audience for a campaign that has not frozen one yet.
 *
 * Returns null when the campaign carries no segment (a one-to-one written
 * against a hand-picked list, or a draft the chat has not finished), which the
 * cell renders as a dash rather than as zero — "no segment" and "nobody
 * matches" are different facts.
 */
export function liveAudience(campaign, segments, collections) {
  const segment = (segments || []).find((s) => str(s.id) === str(campaign?.segmentId));
  if (!segment) return null;
  return { segment, ...resolveSegment(segment, collections) };
}

/**
 * One table row per campaign.
 *
 * @param collections {contacts, clients, jobs, quotes} — passed straight to
 *        the resolver, which is the only thing that reads them.
 */
export function buildCampaignRows({
  campaigns = [], segments = [], contacts = [], clients = [], jobs = [], quotes = [],
  interactions = [], now = new Date(),
} = {}) {
  const collections = { contacts, clients, jobs, quotes, now };

  /* Discarded campaigns leave the catalog. The record stays in Firestore as
     the proof that an initiative was considered and dropped, but the mockup's
     chip row has no "Discarded" filter, so a row nobody can filter to would
     just be noise in the one table Ivin scans. Soft-deleted records go for the
     same reason. */
  return campaigns
    .filter(isV3Campaign)
    .filter((c) => !c.deletedAt && campaignStatus(c) !== "discarded")
    .map((record) => {
      const status = campaignStatus(record);
      const channel = str(record.channel);
      const frozenAt = str(record.audience?.frozenAt);
      const live = frozenAt ? null : liveAudience(record, segments, collections);
      const segmentName = live?.segment?.name
        || (segments.find((s) => str(s.id) === str(record.segmentId))?.name ?? "")
        || (str(record.segmentId) ? str(record.segmentId) : "");

      const audience = frozenAt
        ? {
          frozen: true,
          count: asArray(record.audience?.contactIds).length,
          label: `${asArray(record.audience?.contactIds).length} frozen ${shortDate(frozenAt)}`,
        }
        : live
          ? {
            frozen: false,
            count: live.eligible.length,
            label: `${live.eligible.length} eligible of ${live.members.length} · live`,
          }
          : { frozen: false, count: null, label: "" };

      const replies = campaignReplies({ campaign: record, interactions, contacts, now });
      const delivered = Number(record.results?.delivered) || 0;
      const opened = Number(record.results?.opened) || 0;
      const openedPct = delivered ? Math.round((opened / delivered) * 100) : 0;
      const replyCount = replies.length || Number(record.results?.replied) || 0;
      const measured = ["sent", "measured"].includes(status) && (delivered > 0 || replyCount > 0);

      return {
        id: str(record.id),
        record,
        name: str(record.name) || "Untitled campaign",
        channel,
        channelLabel: CAMPAIGN_CHANNELS[channel] || channel,
        subject: str(record.copy?.subject),
        meta: [CAMPAIGN_CHANNELS[channel] || channel, str(record.copy?.subject) || (status === "draft" ? "draft, no copy yet" : "")]
          .filter(Boolean).join(" · "),
        segmentName,
        audience,
        status,
        statusLabel: CAMPAIGN_STATUSES[status],
        tone: CAMPAIGN_STATUS_TONE[status],
        origin: {
          label: originLabel(record),
          chat: str(record.origin?.source) === "chat",
        },
        when: whenLabel(record, status, channel),
        results: {
          measured,
          openedPct,
          bar: barPct(openedPct, 100),
          replies: replyCount,
          replyRows: replies,
        },
        sortAt: str(record.send?.sentAt) || str(record.schedule?.at) || str(record.createdAt) || str(record.updatedAt) || "",
      };
    })
    .sort((a, b) => (a.sortAt < b.sortAt ? 1 : a.sortAt > b.sortAt ? -1 : 0));
}

/** "/crm campaign · Sep 6" for a chat draft, "Ivin · Sep 1" for a hand-made one. */
export function originLabel(record) {
  const at = shortDate(record?.origin?.at || record?.createdAt);
  const chat = str(record?.origin?.source) === "chat";
  const who = chat ? "/crm campaign" : (str(record?.origin?.by) || str(record?.createdBy) || "Ivin").split("@")[0];
  return at ? `${who} · ${at}` : who;
}

/** The When column: what date this campaign is about, and what kind of date. */
export function whenLabel(record, status, channel) {
  if (channel === "one_to_one" && !["draft", "discarded"].includes(status)) return "rolling";
  if (["sent", "measured"].includes(status)) return shortDate(record?.send?.sentAt) || "sent";
  if (status === "scheduled") return shortDate(record?.schedule?.at) || "scheduled";
  const proposed = shortDate(record?.schedule?.at);
  if (proposed && ["review", "approved"].includes(status)) return `proposed ${proposed}`;
  return "—";
}

/* ── chips ───────────────────────────────────────────────────────────────── */

/* The mockup's filter row. "Check-ins" is not a campaign filter at all — it
   selects the running row, which is why it hides the table rather than
   narrowing it. */
export const CAMPAIGN_CHIPS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "review", label: "Awaiting approval" },
  { key: "scheduled", label: "Scheduled" },
  { key: "sent", label: "Sent" },
  { key: "one_to_one", label: "One-to-one" },
  { key: "checkins", label: "Check-ins" },
];

export function filterCampaignRows(rows, chipKey) {
  switch (chipKey) {
    case "draft": return rows.filter((r) => r.status === "draft");
    case "review": return rows.filter((r) => ["review", "approved"].includes(r.status));
    case "scheduled": return rows.filter((r) => r.status === "scheduled");
    case "sent": return rows.filter((r) => ["sent", "measured"].includes(r.status));
    case "one_to_one": return rows.filter((r) => r.channel === "one_to_one");
    case "checkins": return [];
    default: return rows;
  }
}

/** "11 campaigns · 14 check-ins this month" — the count line. */
export function countLine(rows, checkIns) {
  const n = rows.length;
  const campaigns = `${n.toLocaleString()} campaign${n === 1 ? "" : "s"}`;
  if (!checkIns?.thisMonth) return campaigns;
  return `${campaigns} · ${checkIns.thisMonth} check-in${checkIns.thisMonth === 1 ? "" : "s"} this month`;
}


/* ── the drawer's send time ──────────────────────────────────────────────── */

/**
 * What the schedule picker should hold, and when to change it.
 *
 * Pulled out of the component because the bug it fixes lives in exactly the
 * place a component test cannot reach: the drawer's state was reset only when
 * the open campaign's id CHANGED, so a view that mounted with a campaign
 * already open never reset at all and the picker stayed empty — and an empty
 * `at` handed to Approve wiped the campaign's own proposed time.
 *
 * The key names both the campaign and where the value came from, which also
 * covers the second case: the record arrives asynchronously while the
 * collection loads, so the first render with this id usually has no schedule
 * on it yet and the picker has to pick the record's time up when it lands.
 *
 * @param lastKey the key the current value was derived from ("" on mount)
 * @returns null when nothing should change, else { key, value }
 */
export function resolveSendAt({ openId = "", recordAt = "", lastKey = "", fallback } = {}) {
  const key = openId ? `${openId}|${str(recordAt) ? "record" : "default"}` : "";
  if (key === lastKey) return null;                 // including "no campaign open"
  if (!openId) return { key, value: "" };
  /* A time the operator typed is left alone: the key only changes when the
     campaign changes or when its stored time appears, never on a re-render. */
  return { key, value: str(recordAt) || str(typeof fallback === "function" ? fallback() : fallback) };
}
